import * as React from 'react';
import { map } from 'lodash';
import { SortDirection, FixedSortDirection, ITableColumn } from './types';
import { CheckBox } from '../controls/checkbox';
import { FilterDialog } from '@components/data-table/filters/popoverDialog';
import classNames from 'classnames';

export interface IProps {
  columns: ITableColumn[],
  sortDirection?: SortDirection,
  sortedColumnIndex?: number,
  onSort?: (index: number, direction: SortDirection, column: ITableColumn) => void,
  toggleSort?: boolean,
  hasCheckBox: boolean,
  checkBoxChecked?: boolean,
  checkBoxOnChange?: () => void
}

export function DataTableHeader(props: IProps) {
  const [filterColumn, setFilterColumn] = React.useState(null);

  const cells = map(props.columns, (column: ITableColumn, index: number) => {
    const textAlignClass = column.textAlign ? `text-${column.textAlign}` : '';

    const filterIsOpen = filterColumn === column.id;

    const sortClick = () => {
      if (!props.onSort) { return; }
      const direction = column.sortDirection || nextDirection(props.sortDirection, props.toggleSort);
      props.onSort(index, direction, column);
    };

    return <th data-key={column.id}
      key={column.id + index}
      role="columnheader"
      scope="col"
      style={{
        minWidth: column.minWidth ? `calc(${column.minWidth}${index === 0 && props.hasCheckBox ? ' + 50px' : ''})` : '',
        width: column.width || '',
        cursor: column.sortable ? 'pointer' : 'default'
      }}
      className={classNames(textAlignClass)}
      onClick={column.sortable ? sortClick : undefined}
    >
      {index === 0 && props.hasCheckBox &&
        <CheckBox
          className="fancy-checkbox"
          name={"table-header-checkbox"}
          checked={props.checkBoxChecked}
          onChange={props.checkBoxOnChange}
        />}
      {column.title}
      {column.sortable && <span className='sort-up-down'>
        {getSortIcon(props.sortedColumnIndex === index ? props.sortDirection : undefined, column.sortDirection)}
      </span>}
      {column.FilterForm && <FilterDialog
        columnId={column.id}
        isOpen={filterIsOpen}
        onToggle={() => {
          setFilterColumn(filterIsOpen ? null : column.id);
        }}
        onClose={(referenceElement) => {
          setFilterColumn(null);
          referenceElement?.focus();
        }}
        placement={'bottom-start'}
        popoverPortalSelector=".stateSwitch.selected .card-body"
        FilterForm={column.FilterForm}
      />}
    </th>;
  });

  return (
    <thead>
      <tr>
        {cells}
      </tr>
    </thead>
  );
}

function getSortIcon(direction: SortDirection, fixedDirection?: FixedSortDirection) {
  // fixed-direction columns (e.g. count columns that can only be sorted one way) only ever
  // render the icon matching that direction, since the other direction is never reachable
  if (fixedDirection === 'asc') {
    return <i data-id="sort-up" className={classNames('fas fa-sort-up', { 'active-sort': direction === 'asc' })} />;
  }
  if (fixedDirection === 'desc') {
    return <i data-id="sort-down" className={classNames('fas fa-sort-down', { 'active-sort': direction === 'desc' })} />;
  }
  switch (direction) {
  case 'asc':
    return <><i data-id="sort-up" className={'fas fa-sort-up active-sort'} /><i data-id="sort-down" className={'fas fa-sort-down'} /></>;
  case 'desc':
    return <><i data-id="sort-up" className={'fas fa-sort-up'} /><i data-id="sort-down" className={'fas fa-sort-down active-sort'} /></>;
  default:
    return <><i data-id="sort-up" className={'fas fa-sort-up'} /><i data-id="sort-down" className={'fas fa-sort-down'} /></>;
  }
}

function nextDirection(current: SortDirection, toggle: boolean): SortDirection {
  if (toggle) {
    switch (current) {
    case 'asc': return 'desc';
    case 'desc': return 'asc';
    default: return 'asc';
    }
  }
  switch (current) {
  case 'asc': return 'desc';
  case 'desc': return undefined;
  default: return 'asc';
  }
}
